/**
 * Hidden page for trying asset-extraction knobs on one paper.
 * The menu does not link here.
 *
 * @format
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FLAG_ASSET_PLAYGROUND } from "../../featureFlags/constants";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import {
	ASSET_PLAYGROUND_MODELS,
	PLAYGROUND_NUMBER_FIELDS,
	clusterTuneOf,
	defaultPlaygroundSettings,
	deletePlaygroundVariation,
	readPlaygroundDraft,
	readPlaygroundVariations,
	savePlaygroundVariation,
	writePlaygroundDraft,
	type AssetPlaygroundSettings,
	type NamedPlaygroundVariation,
} from "./assetPlaygroundSettings";
import { askPlaygroundModel, drawPlaygroundFigures, type PlaygroundFigureCard } from "./drawPlaygroundFigures";
import { figureRasterRegion, type ClusteredFigure, type FigureRasterSettings } from "@question-bank/shared";

const fieldClass = "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900";

const FigurePreview = ({ svg, label }: { svg: string; label: string }) => {
	const [url, setUrl] = useState<string>();

	useEffect(() => {
		const next = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
		setUrl(next);
		return () => URL.revokeObjectURL(next);
	}, [svg]);

	if (!url) {
		return null;
	}
	return <img src={url} alt={label} className="max-h-[480px] w-full bg-white object-contain" />;
};

export const AdminAssetPlaygroundPage = () => {
	const { t } = useTranslation();
	const isEnabled = useFeatureFlag(FLAG_ASSET_PLAYGROUND);
	const [settings, setSettings] = useState<AssetPlaygroundSettings>(readPlaygroundDraft);
	const [variations, setVariations] = useState<NamedPlaygroundVariation[]>(readPlaygroundVariations);
	const [variationName, setVariationName] = useState("");
	const [hasNameError, setHasNameError] = useState(false);
	const [paperFile, setPaperFile] = useState<File | null>(null);
	const [isRunning, setIsRunning] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [cards, setCards] = useState<PlaygroundFigureCard[] | null>(null);

	useEffect(() => {
		if (!isEnabled) {
			return;
		}
		writePlaygroundDraft(settings);
	}, [isEnabled, settings]);

	if (!isEnabled) {
		return null;
	}

	const takeFile = (file: File | undefined): void => {
		if (!file) {
			return;
		}
		setPaperFile(file);
		setErrorMessage("");
	};

	const onExtract = async (): Promise<void> => {
		if (!paperFile) {
			return;
		}
		setIsRunning(true);
		setErrorMessage("");
		try {
			const bytes = new Uint8Array(await paperFile.arrayBuffer());
			const [{ readPdfPages, readPdfFigures, readPdfFigureParts, renderPdfFigureRegions, mergeFiguresByCaption }] =
				await Promise.all([import("@question-bank/shared/pdf")]);
			const pages = await readPdfPages(bytes);
			const extracted = await readPdfFigures(bytes, clusterTuneOf(settings));
			const parts = await readPdfFigureParts(bytes, clusterTuneOf(settings));
			// Merge figure fragments that share a caption ("Image 9.1", the
			// "Graphs 4.1 and 4.2" pair) into one asset, as the pipeline does.
			// Parts are undersized fragments a caption can rejoin to a figure.
			const figures = mergeFiguresByCaption(extracted, pages, parts);
			const pageLimit = settings.maxPages > 0 ? settings.maxPages : Number.POSITIVE_INFINITY;
			const shown = figures.filter((figure) => figure.page <= pageLimit);
			const scale = settings.rasterScale > 0 ? settings.rasterScale : 1;

			// Cards carry the candidate and included word boxes, so trimmed crops
			// can be rendered from the same words that build the SVG.
			const cards = await drawPlaygroundFigures(settings, pages, shown, askPlaygroundModel);
			const fkeyOf = (figure: ClusteredFigure): string =>
				`${figure.page}:${Math.round(figure.x)}:${Math.round(figure.y)}`;
			const cardFor = (figure: ClusteredFigure) =>
				cards.find((card) => card.page === figure.page && card.beforeSvg === figure.svg);

			const rasterSettings: FigureRasterSettings = {
				scale: settings.rasterScale,
				padPctX: settings.rasterPadX,
				padPctY: settings.rasterPadY,
				trimMargin: settings.rasterTrimMargin,
				trimReach: settings.rasterTrimReach,
			};
			// The trimmed raster region is built by the shared `figureRasterRegion`,
			// the same helper the import pipeline uses, so the two paths can not
			// drift. The playground passes its tunable settings; production passes
			// the frozen ones.
			const trimIncludedRegions = shown.map((figure) =>
				figureRasterRegion(figure, cardFor(figure)?.includedBoxes ?? [], rasterSettings),
			);

			const keyOf = (raster: { page: number; box: { x: number; y: number } }): string =>
				`${raster.page}:${Math.round(raster.box.x)}:${Math.round(raster.box.y)}`;
			const trimIncluded = await renderPdfFigureRegions(bytes, trimIncludedRegions, scale);
			const mapOf = (rasters: { page: number; box: { x: number; y: number }; png?: string }[]) =>
				new Map(rasters.map((raster) => [keyOf(raster), raster.png]));
			const includedMap = mapOf(trimIncluded);

			setCards(
				cards.map((card) => {
					const figure = shown.find((item) => item.page === card.page && item.svg === card.beforeSvg);
					const fkey = figure ? fkeyOf(figure) : `${card.page}:0:0`;
					return {
						...card,
						...(includedMap.get(fkey) ? { rasterTrimIncludedPng: includedMap.get(fkey) } : {}),
					};
				}),
			);
		} catch (error) {
			setCards(null);
			setErrorMessage(error instanceof Error ? error.message : t("assetPlayground.extractFailed"));
		} finally {
			setIsRunning(false);
		}
	};

	const onSave = (): void => {
		if (variationName.trim().length === 0) {
			setHasNameError(true);
			return;
		}
		setHasNameError(false);
		setVariations(savePlaygroundVariation(variationName, settings));
	};

	return (
		<section className="space-y-8">
			<div>
				<h2 className="text-[1.75rem] font-bold text-(--admin-color-primary)">{t("assetPlayground.title")}</h2>
				<p className="mt-2 max-w-3xl text-sm text-slate-600">{t("assetPlayground.intro")}</p>
				<p className="mt-2 max-w-3xl text-sm text-slate-600">{t("assetPlayground.liveNote")}</p>
			</div>

			<div
				className="rounded-lg border border-dashed border-slate-400 bg-white p-6"
				onDragOver={(event) => event.preventDefault()}
				onDrop={(event) => {
					event.preventDefault();
					takeFile(event.dataTransfer.files[0]);
				}}
			>
				<label className="block text-sm font-semibold text-slate-800" htmlFor="asset-playground-paper">
					{t("assetPlayground.paper")}
				</label>
				<p className="mt-1 text-sm text-slate-600">{t("assetPlayground.drop")}</p>
				<input
					id="asset-playground-paper"
					className="mt-3 block text-sm"
					type="file"
					accept="application/pdf"
					onChange={(event) => takeFile(event.target.files?.[0])}
				/>
				{paperFile && <p className="mt-2 text-sm text-slate-700">{paperFile.name}</p>}
			</div>

			<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
				{PLAYGROUND_NUMBER_FIELDS.map((field) => (
					<label
						key={field.key}
						className="block text-sm font-semibold text-slate-800"
						title={t(`assetPlayground.tips.${field.key}`)}
					>
						{t(field.labelKey)}
						<input
							className={fieldClass}
							type="number"
							step={field.step}
							title={t(`assetPlayground.tips.${field.key}`)}
							value={settings[field.key]}
							onChange={(event) => {
								const value = Number(event.target.value);
								setSettings((current) => ({
									...current,
									[field.key]: Number.isFinite(value) ? value : current[field.key],
								}));
							}}
						/>
					</label>
				))}
				<label
					className="block text-sm font-semibold text-slate-800"
					htmlFor="asset-playground-language"
					title={t("assetPlayground.tips.language")}
				>
					{t("assetPlayground.language")}
					<select
						id="asset-playground-language"
						className={fieldClass}
						title={t("assetPlayground.tips.language")}
						value={settings.language}
						onChange={(event) =>
							setSettings((current) => ({
								...current,
								language: event.target.value === "cy" ? "cy" : "en",
							}))
						}
					>
						<option value="en">{t("assetPlayground.languageEn")}</option>
						<option value="cy">{t("assetPlayground.languageCy")}</option>
					</select>
				</label>
				<label
					className="block text-sm font-semibold text-slate-800"
					htmlFor="asset-playground-model"
					title={t("assetPlayground.tips.model")}
				>
					{t("assetPlayground.model")}
					<select
						id="asset-playground-model"
						className={fieldClass}
						title={t("assetPlayground.tips.model")}
						value={settings.model}
						onChange={(event) => {
							const model = ASSET_PLAYGROUND_MODELS.find((item) => item === event.target.value) ?? "lite";
							setSettings((current) => ({ ...current, model }));
						}}
					>
						{ASSET_PLAYGROUND_MODELS.map((model) => (
							<option key={model} value={model}>
								{model}
							</option>
						))}
					</select>
				</label>
			</div>

			<label
				className="flex items-center gap-2 text-sm font-semibold text-slate-800"
				title={t("assetPlayground.tips.askModel")}
			>
				<input
					type="checkbox"
					checked={settings.shouldAskModel}
					onChange={(event) => setSettings((current) => ({ ...current, shouldAskModel: event.target.checked }))}
				/>
				{t("assetPlayground.askModel")}
			</label>

			<label
				className="block text-sm font-semibold text-slate-800"
				htmlFor="asset-playground-captions"
				title={t("assetPlayground.tips.captionWords")}
			>
				{t("assetPlayground.captionWords")}
				<textarea
					id="asset-playground-captions"
					className={`${fieldClass} min-h-36 font-mono`}
					title={t("assetPlayground.tips.captionWords")}
					value={settings.captionWords}
					onChange={(event) => setSettings((current) => ({ ...current, captionWords: event.target.value }))}
				/>
			</label>
			<p className="text-sm text-slate-600">{t("assetPlayground.captionHint")}</p>

			<label
				className="block text-sm font-semibold text-slate-800"
				htmlFor="asset-playground-prompt"
				title={t("assetPlayground.tips.prompt")}
			>
				{t("assetPlayground.prompt")}
				<textarea
					id="asset-playground-prompt"
					className={`${fieldClass} min-h-40`}
					title={t("assetPlayground.tips.prompt")}
					value={settings.prompt}
					onChange={(event) => setSettings((current) => ({ ...current, prompt: event.target.value }))}
				/>
			</label>
			<p className="text-sm text-slate-600">{t("assetPlayground.promptHint")}</p>

			<label
				className="block text-sm font-semibold text-slate-800"
				htmlFor="asset-playground-line"
				title={t("assetPlayground.tips.lineFormat")}
			>
				{t("assetPlayground.lineFormat")}
				<textarea
					id="asset-playground-line"
					className={`${fieldClass} min-h-20 font-mono`}
					title={t("assetPlayground.tips.lineFormat")}
					value={settings.lineFormat}
					onChange={(event) => setSettings((current) => ({ ...current, lineFormat: event.target.value }))}
				/>
			</label>
			<p className="text-sm text-slate-600">{t("assetPlayground.lineFormatHint")}</p>

			<div className="flex flex-wrap items-end gap-3">
				<label className="block text-sm font-semibold text-slate-800" htmlFor="asset-playground-variation">
					{t("assetPlayground.variationName")}
					<input
						id="asset-playground-variation"
						className={fieldClass}
						type="text"
						value={variationName}
						onChange={(event) => setVariationName(event.target.value)}
					/>
				</label>
				<button
					type="button"
					className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800"
					onClick={onSave}
				>
					{t("assetPlayground.saveVariation")}
				</button>
				<button
					type="button"
					className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-800"
					onClick={() => {
						setSettings(defaultPlaygroundSettings());
						setHasNameError(false);
					}}
				>
					{t("assetPlayground.reset")}
				</button>
				<button
					type="button"
					className="rounded-md bg-(--admin-color-primary) px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
					disabled={paperFile === null || isRunning}
					onClick={() => void onExtract()}
				>
					{isRunning ? t("assetPlayground.extracting") : t("assetPlayground.extract")}
				</button>
			</div>
			{hasNameError && (
				<p role="alert" className="text-sm text-red-800">
					{t("assetPlayground.nameRequired")}
				</p>
			)}

			<div>
				<h3 className="text-lg font-semibold text-slate-800">{t("assetPlayground.savedVariations")}</h3>
				{variations.length === 0 ? (
					<p className="mt-2 text-sm text-slate-600">{t("assetPlayground.noVariations")}</p>
				) : (
					<ul className="mt-3 space-y-2">
						{variations.map((variation) => (
							<li key={variation.name} className="flex flex-wrap items-center gap-2">
								<span className="text-sm text-slate-800">{variation.name}</span>
								<button
									type="button"
									className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"
									onClick={() => setSettings(variation.settings)}
								>
									{t("assetPlayground.loadVariation")}
								</button>
								<button
									type="button"
									className="rounded-md border border-slate-300 bg-white px-3 py-1 text-sm"
									onClick={() => setVariations(deletePlaygroundVariation(variation.name))}
								>
									{t("assetPlayground.deleteVariation")}
								</button>
							</li>
						))}
					</ul>
				)}
			</div>

			{errorMessage && (
				<p role="alert" className="text-sm text-red-800">
					{errorMessage}
				</p>
			)}

			{cards && cards.length === 0 && <p className="text-sm text-slate-600">{t("assetPlayground.noFigures")}</p>}

			{cards && cards.length > 0 && (
				<ol className="space-y-8">
					{cards.map((card, index) => {
						const label = t("assetPlayground.pageFigure", { page: card.page, index: index + 1 });
						return (
							<li key={`${card.page}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
								<h3 className="text-base font-semibold text-slate-800">{label}</h3>
								<p className="mt-1 text-sm text-slate-600">{t("assetPlayground.reviewGuide")}</p>
								<div className="mt-3 grid gap-4 lg:grid-cols-3">
									<figure className="rounded-md border-2 border-amber-400 bg-white p-3">
										<figcaption className="flex items-center gap-2 text-sm font-semibold text-slate-800">
											{t("assetPlayground.after")}
											<span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
												{t("assetPlayground.candidateBadge")}
											</span>
										</figcaption>
										<p className="mt-1 text-sm text-slate-600">{t("assetPlayground.afterNote")}</p>
										<div className="mt-3">
											<FigurePreview svg={card.svg} label={`${label}. ${t("assetPlayground.after")}`} />
										</div>
									</figure>
									{card.svgWithModel !== undefined && (
										<figure className="rounded-md border border-slate-300 bg-white p-3">
											<figcaption className="flex items-center gap-2 text-sm font-semibold text-slate-800">
												{t("assetPlayground.withModel")}
												<span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700">
													{t("assetPlayground.withModelBadge")}
												</span>
											</figcaption>
											<p className="mt-1 text-sm text-slate-600">
												{card.modelError ? card.modelError : t("assetPlayground.withModelNote")}
											</p>
											<div className="mt-3">
												<FigurePreview svg={card.svgWithModel} label={`${label}. ${t("assetPlayground.withModel")}`} />
											</div>
										</figure>
									)}
									<figure className="rounded-md border-2 border-green-500 bg-white p-3">
										<figcaption className="flex items-center gap-2 text-sm font-semibold text-slate-800">
											{t("assetPlayground.rasterTrimIncluded")}
											<span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-800">
												{t("assetPlayground.recommendedBadge")}
											</span>
										</figcaption>
										<p className="mt-1 text-sm text-slate-600">{t("assetPlayground.rasterTrimIncludedNote")}</p>
										<div className="mt-3">
											{card.rasterTrimIncludedPng ? (
												<img
													src={card.rasterTrimIncludedPng}
													alt={`${label}. ${t("assetPlayground.rasterTrimIncluded")}`}
													className="mx-auto max-h-[480px] max-w-full bg-white object-contain"
												/>
											) : (
												<p className="text-sm text-slate-600">{t("assetPlayground.rasterMissing")}</p>
											)}
										</div>
									</figure>
								</div>
								<p className="mt-3 text-sm text-slate-700">
									{t("assetPlayground.candidates")}: {card.candidates.join(", ") || t("assetPlayground.none")}
								</p>
								{(() => {
									const modelKept = card.includedWithModel ? new Set(card.includedWithModel) : undefined;
									return (
										<>
											<p className="text-sm text-slate-700">
												<span className="font-semibold">{t("assetPlayground.included")}:</span>{" "}
												{card.included.length === 0 ? (
													t("assetPlayground.none")
												) : (
													<span className="inline-flex flex-wrap gap-1 align-middle">
														{card.included.map((word, wordIndex) => {
															// When the model ran, highlight words it dropped so the
															// difference from the model subset is obvious.
															const isDropped = modelKept !== undefined && !modelKept.has(word);
															return (
																<span
																	key={`drawn-${wordIndex}`}
																	className={
																		isDropped
																			? "rounded bg-amber-100 px-1 text-amber-900 line-through"
																			: "rounded bg-slate-100 px-1 text-slate-700"
																	}
																>
																	{word}
																</span>
															);
														})}
													</span>
												)}
											</p>
											{card.includedWithModel !== undefined && (
												<p className="mt-1 text-sm text-slate-700">
													<span className="font-semibold">{t("assetPlayground.includedWithModel")}:</span>{" "}
													{card.includedWithModel.length === 0 ? (
														t("assetPlayground.none")
													) : (
														<span className="inline-flex flex-wrap gap-1 align-middle">
															{card.includedWithModel.map((word, wordIndex) => (
																<span key={`model-${wordIndex}`} className="rounded bg-green-100 px-1 text-green-800">
																	{word}
																</span>
															))}
														</span>
													)}
												</p>
											)}
										</>
									);
								})()}
								<details className="mt-2">
									<summary className="cursor-pointer text-sm font-semibold text-slate-800">
										{t("assetPlayground.promptUsed")}
									</summary>
									<pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-xs text-slate-700">{card.prompt}</pre>
								</details>
							</li>
						);
					})}
				</ol>
			)}
		</section>
	);
};
